import * as React from "react";
import { DayPicker, type DayButtonProps } from "react-day-picker";
import ChevronLeftIcon from "@src/lib/icons/18px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/18px/chevron-right";
import ChevronDownIcon from "@src/lib/icons/12px/chevron-down";

import { cn } from "@src/lib/utils/cn";
import { buttonVariants } from "@src/lib/ui/Button";

/** Day-cell size. Every cell, weekday column and range band reads
 * `--cell-size`, so this one value scales the whole grid. */
const CALENDAR_CELL_SIZES = {
  default: "[--cell-size:2rem]",
  large: "[--cell-size:2.5rem]",
} as const;

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /** 32px cells by default; `large` (40px) for a roomier standalone picker. */
  cellSize?: keyof typeof CALENDAR_CELL_SIZES;
};

const NAV_BUTTON_CLASS = cn(
  buttonVariants({ variant: "ghost", size: "icon-sm" }),
  "text-icon-secondary select-none aria-disabled:pointer-events-none aria-disabled:opacity-50",
);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  cellSize = "default",
  formatters,
  components,
  ...props
}: CalendarProps): React.ReactElement {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn(
        "group/calendar w-fit p-3",
        CALENDAR_CELL_SIZES[cellSize],
        className,
      )}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        // `relative` anchors the nav, which v9 renders as the first child of
        // `months`, so prev sits at the far left of the first month and next
        // at the far right of the last.
        months: "relative flex flex-col gap-4 sm:flex-row sm:gap-6",
        month: "flex w-full flex-col gap-1",
        nav: "absolute inset-x-0 top-0 flex h-8 items-center justify-between",
        button_previous: NAV_BUTTON_CLASS,
        button_next: NAV_BUTTON_CLASS,
        chevron: "size-4",
        month_caption: "flex h-8 w-full items-center justify-center px-8",
        dropdowns:
          "flex h-8 w-full items-center justify-center gap-1.5 text-s-v4 font-medium-v4 text-text-primary",
        dropdown_root:
          "relative rounded-md has-focus:ring-[3px] has-focus:ring-brand-border",
        dropdown: "absolute inset-0 opacity-0",
        caption_label: cn(
          "text-s-v4 font-medium-v4 text-text-primary select-none",
          captionLayout !== "label" &&
            "flex h-7 items-center gap-1 rounded-md pr-1 pl-2 [&>svg]:size-3 [&>svg]:text-icon-secondary",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "flex h-7 w-(--cell-size) items-center justify-center text-xs-v4 font-normal-v4 text-text-tertiary select-none",
        week: "mt-0.5 flex w-full",
        week_number_header: "w-(--cell-size) select-none",
        week_number:
          "flex size-(--cell-size) items-center justify-center text-xs-v4 text-text-tertiary select-none",
        // The cell carries the range band; the DayButton inside carries every
        // per-day state (today, selected, caps, outside, disabled). Outside
        // days drop the band, so a range crossing a month boundary rounds the
        // in-month cell next to an outside one, as it does at row ends.
        day: "group/day relative size-(--cell-size) p-0 text-center select-none [&:first-child[data-selected=true]]:rounded-l-lg [&:last-child[data-selected=true]]:rounded-r-lg [[data-outside]+&[data-selected=true]]:rounded-l-lg [&[data-selected=true]:has(+[data-outside])]:rounded-r-lg data-outside:bg-transparent",
        range_start: "rounded-l-lg bg-brand-bg",
        range_middle: "bg-brand-bg",
        range_end: "rounded-r-lg bg-brand-bg",
        selected: "",
        today: "",
        outside: "",
        disabled: "",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevronClassName, orientation }) => {
          if (orientation === "left") {
            return <ChevronLeftIcon className={chevronClassName} />;
          }
          if (orientation === "right") {
            return <ChevronRightIcon className={chevronClassName} />;
          }
          return <ChevronDownIcon className="size-3" />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: DayButtonProps): React.ReactElement {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isRangeStart = Boolean(modifiers.range_start);
  const isRangeEnd = Boolean(modifiers.range_end);
  const isRangeMiddle = Boolean(modifiers.range_middle);
  const isSelectedSingle =
    Boolean(modifiers.selected) &&
    !isRangeStart &&
    !isRangeEnd &&
    !isRangeMiddle;
  const isFilled = isSelectedSingle || isRangeStart || isRangeEnd;

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString()}
      data-today={(modifiers.today && !isFilled) || undefined}
      data-filled={isFilled || undefined}
      data-range-middle={isRangeMiddle || undefined}
      data-outside={modifiers.outside || undefined}
      data-disabled={modifiers.disabled || undefined}
      className={cn(
        "relative flex size-(--cell-size) items-center justify-center rounded-lg p-0 text-s-v4 font-normal-v4 text-text-secondary outline-none select-none",
        "hover:bg-state-hover active:bg-state-pressed",
        "focus-visible:z-10 focus-visible:ring-[3px] focus-visible:ring-brand-border",
        "data-today:bg-bg-tertiary data-today:text-text-primary data-today:hover:bg-state-hover data-today:active:bg-state-pressed",
        "data-range-middle:text-brand-text",
        "data-outside:not-data-filled:text-text-disabled",
        "data-filled:bg-action-primary data-filled:text-action-primary-foreground data-filled:hover:bg-action-primary-hover data-filled:active:bg-action-primary-active",
        "data-disabled:pointer-events-none data-disabled:text-text-disabled",
        className,
      )}
      {...props}
    />
  );
}

export { Calendar };
